/**
 * Order a list of entities by the user's custom order (an array of ids), with
 * the entity's global `displayOrder` as the fallback. Reusable across every
 * catalog that has a per-user order (Company today; tags, pins, … later).
 *
 *   - Items the user explicitly ordered come first, in that order.
 *   - Items NOT in the user's order (e.g. created after they ordered) go after,
 *     by `displayOrder` — so new items land at the end predictably.
 *   - Ids in `orderedIds` that no longer exist are simply ignored.
 *   - No custom order → falls back entirely to `displayOrder`.
 *
 * Pure: returns a new array, never mutates the input.
 *
 * @param {Array<{ id: string|number, displayOrder?: number }>} items
 * @param {Array<string|number>} [orderedIds]  the user's preferred id order
 * @returns {Array} sorted copy
 */
export function applyUserOrder(items, orderedIds) {
  const list = Array.isArray(items) ? items : []
  const byDisplayOrder = (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)

  if (!Array.isArray(orderedIds) || orderedIds.length === 0) {
    return [...list].sort(byDisplayOrder)
  }

  const pos = new Map(orderedIds.map((id, i) => [String(id), i]))

  return [...list].sort((a, b) => {
    const pa = pos.has(String(a.id)) ? pos.get(String(a.id)) : Infinity
    const pb = pos.has(String(b.id)) ? pos.get(String(b.id)) : Infinity

    return pa !== pb ? pa - pb : byDisplayOrder(a, b)
  })
}
